import { Link, useCanGoBack, useRouter } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { cx } from './cx.js';
import { ChevronLeftIcon } from './icons.js';

/**
 * Retour (flèche en haut à gauche, zone tactile de 44 px). Si l'écran a été ouvert depuis un autre
 * écran de l'application, il y revient (historique du navigateur : filtres, onglet et position
 * conservés) et s'intitule « Retour » ; sinon (lien ouvert directement, nouvel onglet), il mène à
 * l'écran parent `to`, nommé par `label`. `exact` : toujours l'écran parent (ex. sections du Compte).
 */
export function BackLink({ to, label, className, exact = false }: { to: string; label: string; className?: string; exact?: boolean }) {
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const back = canGoBack && !exact;
  return (
    <Link
      to={to}
      onClick={(e) => {
        if (!back || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        router.history.back();
      }}
      className={cx(
        '-ml-2 inline-flex min-h-(--ctl-sm) min-w-11 items-center gap-0.5 rounded-md pr-2.5 pl-1 text-sm font-medium text-ink-2 transition-colors hover:bg-sunken hover:text-ink',
        className,
      )}
    >
      <ChevronLeftIcon size={16} />
      {back ? 'Retour' : label}
    </Link>
  );
}

/**
 * En-tête de page : retour éventuel, titre (h1), description, actions. Sur téléphone, les actions
 * passent sous le titre en pleine largeur ; sur ordinateur, elles s'alignent à droite.
 */
export function PageHeader({
  title,
  description,
  back,
  actions,
  meta,
  help,
  className,
}: {
  title: ReactNode;
  /** Lien contextuel « ? » vers l'article d'aide de l'écran (`help/HelpLink`), à droite du titre. */
  help?: ReactNode;
  description?: ReactNode;
  back?: { to: string; label: string };
  actions?: ReactNode;
  /** Ligne au-dessus du titre à droite du retour (ex. statut). */
  meta?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cx('mb-4 lg:mb-5', className)}>
      {back || meta ? (
        <div className="mb-1 flex items-center justify-between gap-3">
          {back ? <BackLink to={back.to} label={back.label} /> : <span />}
          {meta}
        </div>
      ) : null}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          {help ? (
            <div className="flex items-center gap-1">
              <h1 className="text-2xl font-semibold tracking-[-0.02em] text-ink">{title}</h1>
              {help}
            </div>
          ) : (
            <h1 className="text-2xl font-semibold tracking-[-0.02em] text-ink">{title}</h1>
          )}
          {description ? (
            <p className="mt-0.5 max-w-[72ch] text-sm text-ink-3">{description}</p>
          ) : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap gap-2 [&>*]:flex-1 sm:[&>*]:flex-none">{actions}</div>
        ) : null}
      </div>
    </header>
  );
}

/** Section de page : titre (h2) discret, action à droite, contenu. */
export function Section({
  title,
  description,
  action,
  children,
  className,
  id,
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section id={id} className={cx('mb-6 last:mb-0', className)}>
      {title || action ? (
        <div className="mb-2 flex min-h-7 items-end justify-between gap-3">
          <div className="min-w-0">
            {title ? <h2 className="text-lg font-semibold tracking-[-0.01em] text-ink">{title}</h2> : null}
            {description ? <p className="mt-0.5 text-sm text-ink-3">{description}</p> : null}
          </div>
          {action}
        </div>
      ) : null}
      {children}
    </section>
  );
}
